/* =========================================================================
   Brand icon library (brand/STYLE-GUIDE.md §8) — hand-drawn line icons that
   replace the emoji chrome (🗂️ ⛪ 🧭 💰 🔥 …) across the UI.

   Every icon is a 24×24 viewBox drawn in confident 2px strokes with round
   caps/joins, inheriting `currentColor` so it takes the deep-teal ink
   (#163B36) from the text it sits in. A few carry a single gold (#F5B914)
   fill accent — bulb glow, star body, flame heart — used sparingly.

   Leaf module: no imports, safe everywhere (markup templates, jsdom tests).
   ========================================================================= */

export type IconName =
  | 'book'
  | 'bulb'
  | 'gamepad'
  | 'trophy'
  | 'candle'
  | 'flame'
  | 'dove'
  | 'church'
  | 'cards'
  | 'compass'
  | 'coin'
  | 'gift'
  | 'clock'
  | 'star'
  | 'medal'
  | 'sparkle'
  | 'cross'
  | 'check'
  | 'sprout'
  | 'branch'
  | 'tree'
  // Chrome icons (2026-09-13): navigation, account and inventory glyphs that
  // replaced the last emoji in the app shell.
  | 'home'
  | 'user'
  | 'bell'
  | 'chat'
  | 'key'
  | 'signout'
  | 'trash'
  | 'pause'
  | 'ticket'
  | 'rosary'
  | 'urn'
  | 'lock';

/* Inner path data per icon. Strokes inherit the svg-level stroke attributes;
   gold accent shapes opt out with stroke="none" + fill="#F5B914". */
const PATHS: Record<IconName, string> = {
  // Growth trio for the difficulty picker (Eric 2026-09-06): sprout → branch →
  // olive tree. Same 2px ink strokes, one muted-sage fill accent each.
  sprout:
    '<path d="M5 20.5h14" />' +
    '<path d="M12 20.5V12" />' +
    '<path d="M12 13.5C12 9.8 9.6 7.6 6 7.4c.2 3.6 2.4 6 6 6.1Z" fill="#A9C49A"/>' +
    '<path d="M12 11.5c0-3.2 2.1-5.1 5.2-5.3-.2 3.1-2.1 5.2-5.2 5.3Z" fill="#A9C49A"/>',
  branch:
    '<path d="M4.5 19.5C9 15 13.5 10.5 19.5 4.8" />' +
    '<path d="M9.6 14.4c-.4-2.4-2-3.7-4.4-3.8.3 2.4 1.9 3.8 4.4 3.8Z" fill="#A9C49A"/>' +
    '<path d="M12.9 11.1c2.4.4 3.7 2 3.8 4.4-2.4-.3-3.8-1.9-3.8-4.4Z" fill="#A9C49A"/>' +
    '<path d="M15.3 8.7c-.4-2.4-2-3.7-4.4-3.8.3 2.4 1.9 3.8 4.4 3.8Z" fill="#A9C49A"/>',
  tree:
    '<path d="M12 21v-6.5" />' +
    '<path d="M12 16.5c-1.4-1.2-3-1.7-5-1.6" />' +
    '<path d="M12 15c1.2-1.4 2.7-2 4.6-2" />' +
    '<path d="M12 3.6c-4.4 0-7.4 2.6-7.4 5.8 0 2.5 1.9 4.4 4.6 4.9 1 .2 1.9.2 2.8.2s1.8 0 2.8-.2c2.7-.5 4.6-2.4 4.6-4.9 0-3.2-3-5.8-7.4-5.8Z" fill="#A9C49A"/>' +
    '<path d="M5 21h14" />',
  // Check in a filled teal roundel — the brand "correct" mark (white tick on
  // deep teal, drawn a touch off-square so it reads hand-made, not system).
  check:
    '<circle cx="12" cy="12" r="10.2" fill="#1D5C56" stroke="none"/>' +
    '<path d="M7.2 12.4l3.2 3.4 6.3-7.4" stroke="#FFFFFF" stroke-width="2.6"/>',
  // Open book — two soft-cornered leaves meeting at the spine.
  book:
    '<path d="M12 6.6C10.2 4.9 7.6 4.3 4.5 4.6c-.6.1-1 .6-1 1.2v11.4c0 .7.6 1.2 1.3 1.1 2.7-.3 5 .3 7.2 1.9 2.2-1.6 4.5-2.2 7.2-1.9.7.1 1.3-.4 1.3-1.1V5.8c0-.6-.4-1.1-1-1.2-3.1-.3-5.7.3-7.5 2Z"/>' +
    '<path d="M12 6.6v13.6"/>',
  // Lightbulb with a warm gold glow inside.
  bulb:
    '<circle cx="12" cy="10" r="2.7" fill="#F5B914" stroke="none"/>' +
    '<path d="M9.6 17.4c-.2-1.5-.9-2.5-1.9-3.6a5.7 5.7 0 0 1-1.5-3.9 5.8 5.8 0 0 1 11.6 0c0 1.5-.5 2.8-1.5 3.9-1 1.1-1.7 2.1-1.9 3.6Z"/>' +
    '<path d="M9.9 20.5h4.2"/>',
  // Game pad — rounded body, d-pad left, two dot buttons right.
  gamepad:
    '<path d="M7.6 7h8.8a5.5 5.5 0 0 1 5.4 6.5c-.3 1.7-.7 3.2-1.3 4.3-.5 1-1.8 1.3-2.7.6l-2.5-2H8.7l-2.5 2c-.9.7-2.2.4-2.7-.6-.6-1.1-1-2.6-1.3-4.3A5.5 5.5 0 0 1 7.6 7Z"/>' +
    '<path d="M8 10.8v3.4M6.3 12.5h3.4"/>' +
    '<path d="M15.4 11.2v.01M17.7 13.2v.01"/>',
  // Trophy — cup, two handles, stem and foot.
  trophy:
    '<path d="M8 4.5h8v4.8a4 4 0 0 1-8 0Z"/>' +
    '<path d="M8 6H5.2c-.1 2.6 1 4.1 3 4.5M16 6h2.8c.1 2.6-1 4.1-3 4.5"/>' +
    '<path d="M12 13.3v3.5"/>' +
    '<path d="M9.9 20c.2-1.9 1-3.2 2.1-3.2s1.9 1.3 2.1 3.2Z"/>',
  // Votive candle — teardrop flame over a stout body.
  candle:
    '<path d="M12 8.6c1.5-1 2-2.4 1.2-3.7-.3-.5-.7-1.1-1.2-1.9-.5.8-.9 1.4-1.2 1.9-.8 1.3-.3 2.7 1.2 3.7Z"/>' +
    '<path d="M9 11.6h6V20a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1Z"/>' +
    '<path d="M12 14.2v2.6"/>',
  // Streak flame with a gold heart.
  flame:
    '<path d="M12 21c-1.7 0-3-1.4-3-3.1 0-1.5 1-2.7 2.2-4 .4-.5 1.2-.5 1.6 0 1.2 1.3 2.2 2.5 2.2 4 0 1.7-1.3 3.1-3 3.1Z" fill="#F5B914" stroke="none"/>' +
    '<path d="M12 21c3.5 0 6-2.4 6-5.7 0-2.5-1.5-4.4-2.9-6.1-.8-1-1.6-2-2.2-3.2-.2-.5-.9-.6-1.2-.1-.7 1.2-.9 2.5-.6 3.9-1.1-.6-2-1.5-2.5-2.5-.3-.5-1-.5-1.2 0-.9 1.7-1.4 3.4-1.4 5C6 18.5 8.5 21 12 21Z"/>',
  // Dove in flight, wing sweeping back.
  dove:
    '<path d="m20.5 6.6-1.9.4c-.5-1.2-1.7-2-3.1-2-1.9 0-3.4 1.5-3.7 3.4-2.7-.4-5.3.4-7.8 2.4 1.9.6 3.3 1.5 4.2 2.7-.9.4-2 .6-3.1.6 2.3 3.2 5.6 4.4 9 3.3 3.4-1.1 5.3-4 5.2-7.8l1.2-1.9Z"/>' +
    '<path d="M11.8 8.6c-.9 2-2.6 3.3-4.9 4"/>',
  // Small chapel: gabled hall + cross above the ridge + arched door.
  church:
    '<path d="M4.5 20.5v-7.8L12 7.5l7.5 5.2v7.8Z"/>' +
    '<path d="M12 3v4.5M10 4.8h4"/>' +
    '<path d="M10 20.5v-3.7a2 2 0 0 1 4 0v3.7"/>',
  // Two overlapping cards, the front one marked with a small cross.
  cards:
    '<path d="M7 17.9 4.7 7.2a2 2 0 0 1 1.5-2.4l6.3-1.3"/>' +
    '<rect x="7.8" y="6" width="10" height="14" rx="2"/>' +
    '<path d="M12.8 10.2v5M10.8 12.2h4"/>',
  // Compass — bezel ring with a needle diamond.
  compass:
    '<circle cx="12" cy="12" r="8.5"/>' +
    '<path d="m15.3 8.7-1.7 4.9-4.9 1.7 1.7-4.9Z"/>',
  // Coin — outer edge with an inner minted ring.
  coin:
    '<circle cx="12" cy="12" r="8.5"/>' +
    '<circle cx="12" cy="12" r="5"/>',
  // Gift — lidded box, ribbon, two looped bows.
  gift:
    '<path d="M5 11.6h14v7.9a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1Z"/>' +
    '<path d="M4 7.6h16v4H4Z"/>' +
    '<path d="M12 7.6v12.9"/>' +
    '<path d="M12 7.6c-1.5-2.6-3.5-3.4-4.7-2.2C6.1 6.6 7.5 7.6 12 7.6Zm0 0c1.5-2.6 3.5-3.4 4.7-2.2 1.2 1.2-.2 2.2-4.7 2.2Z"/>',
  // Clock at ten past.
  clock:
    '<circle cx="12" cy="12" r="8.5"/>' +
    '<path d="M12 7.4V12l3 2"/>',
  // Five-point star, gold-filled with an ink outline.
  star:
    '<path d="m12 3.6 2.5 5.1 5.6.8-4.1 3.9 1 5.6-5-2.7-5 2.7 1-5.6-4.1-3.9 5.6-.8Z" fill="#F5B914" stroke="none"/>' +
    '<path d="m12 3.6 2.5 5.1 5.6.8-4.1 3.9 1 5.6-5-2.7-5 2.7 1-5.6-4.1-3.9 5.6-.8Z"/>',
  // Medal — disc hanging from two ribbon tails.
  medal:
    '<path d="M8.3 10.6 5.6 4.5h4l1.5 3.3M15.7 10.6l2.7-6.1h-4l-1.5 3.3"/>' +
    '<circle cx="12" cy="14.7" r="5"/>' +
    '<circle cx="12" cy="14.7" r="1.9"/>',
  // Four-point sparkle with a small companion twinkle.
  sparkle:
    '<path d="M11.5 4c.6 3.5 2.3 5.2 5.8 5.8-3.5.6-5.2 2.3-5.8 5.8-.6-3.5-2.3-5.2-5.8-5.8 3.5-.6 5.2-2.3 5.8-5.8Z"/>' +
    '<path d="M18 15.2c.3 1.7 1.2 2.6 2.9 2.9-1.7.3-2.6 1.2-2.9 2.9-.3-1.7-1.2-2.6-2.9-2.9 1.7-.3 2.6-1.2 2.9-2.9Z"/>',
  // Simple latin cross.
  cross: '<path d="M12 4v16M7.8 9.2h8.4"/>',
  /* ---- chrome (navigation / account / inventory) ---- */
  // House — gable, walls, arched door.
  home:
    '<path d="M4.5 11.2 12 4.6l7.5 6.6"/>' +
    '<path d="M6.5 9.6v9.9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V9.6"/>' +
    '<path d="M10 20.5v-5a2 2 0 0 1 4 0v5"/>',
  // Person — head over rounded shoulders.
  user:
    '<circle cx="12" cy="8.2" r="3.7"/>' +
    '<path d="M4.9 20.2c.6-3.6 3.4-5.6 7.1-5.6s6.5 2 7.1 5.6Z"/>',
  // Bell with a clapper and a short crown stem.
  bell:
    '<path d="M6.3 16.8c.9-1.1 1.4-2.4 1.4-3.9v-2.3a4.3 4.3 0 0 1 8.6 0v2.3c0 1.5.5 2.8 1.4 3.9Z"/>' +
    '<path d="M10.2 19.4a1.9 1.9 0 0 0 3.6 0"/>' +
    '<path d="M12 4.3v1.6"/>',
  // Speech bubble with two text lines.
  chat:
    '<path d="M4.5 6.5A1.5 1.5 0 0 1 6 5h12a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 18 16h-7.6l-4.4 3.6V16H6a1.5 1.5 0 0 1-1.5-1.5Z"/>' +
    '<path d="M8.5 9.4h7M8.5 12.2h4.6"/>',
  // Key — bow at the lower left, bit toward the upper right.
  key:
    '<circle cx="8" cy="15.5" r="3.8"/>' +
    '<path d="m10.7 12.8 8.5-8.5"/>' +
    '<path d="m16.4 6.5 2.6 2.6M14 8.9l2.6 2.6"/>',
  // Door frame with an arrow leaving to the right.
  signout:
    '<path d="M13.5 4.5H7A1.5 1.5 0 0 0 5.5 6v12A1.5 1.5 0 0 0 7 19.5h6.5"/>' +
    '<path d="M10.5 12h9M16.5 8.6 20 12l-3.5 3.4"/>',
  // Bin — lid, tapered body, two slats.
  trash:
    '<path d="M5 7h14M9.5 7V5.2a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V7"/>' +
    '<path d="m6.5 7 .8 12a1.2 1.2 0 0 0 1.2 1.1h7a1.2 1.2 0 0 0 1.2-1.1l.8-12"/>' +
    '<path d="M10 11v6M14 11v6"/>',
  // Two rounded bars.
  pause:
    '<rect x="6" y="4.5" width="4" height="15" rx="1.2"/>' +
    '<rect x="14" y="4.5" width="4" height="15" rx="1.2"/>',
  // Admission ticket — notched sides, perforated stub.
  ticket:
    '<path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h13A1.5 1.5 0 0 1 20 8.5v1.8a2 2 0 0 0 0 3.4v1.8a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 15.5v-1.8a2 2 0 0 0 0-3.4Z"/>' +
    '<path d="M9.5 7.5v9" stroke-dasharray="1.6 2.2"/>',
  // Rosary — a loop of beads (dotted stroke) with the pendant cross.
  rosary:
    '<ellipse cx="12" cy="9.2" rx="6" ry="4.8" stroke-width="2.6" stroke-dasharray="0.1 3.1"/>' +
    '<path d="M12 14v2.6"/>' +
    '<path d="M12 16.6v4M10 18.6h4"/>',
  // Reliquary urn — lid knob, shouldered body, foot.
  urn:
    '<path d="M9.5 5h5"/>' +
    '<path d="M8.3 7h7.4c.6 3 1.3 4.9 1.3 7.2a5 5 0 0 1-10 0C7 11.9 7.7 10 8.3 7Z"/>' +
    '<path d="M12 19.2v1.8M9.5 21h5"/>',
  // Padlock — shackle over a rounded body, keyhole slot.
  lock:
    '<rect x="5.5" y="10.5" width="13" height="10" rx="2"/>' +
    '<path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5"/>' +
    '<path d="M12 14.5v2.4"/>',
};

/** True when `s` names a brand icon — lets a catalog/server-supplied icon
    string carry an IconName (rendered as SVG) or a legacy glyph (escaped). */
export function isIconName(s: string | null | undefined): s is IconName {
  return typeof s === 'string' && Object.prototype.hasOwnProperty.call(PATHS, s);
}

/** Inline SVG for a brand icon. Inherits `currentColor`; sits on the text
    baseline (slight optical drop) so it can replace an emoji in-line. */
export function icon(name: IconName, size = 20, cls = ''): string {
  return (
    `<svg class="ui-icon ${cls}" width="${size}" height="${size}" viewBox="0 0 24 24" ` +
    `fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ` +
    `stroke-linejoin="round" aria-hidden="true" style="vertical-align:-0.18em">` +
    PATHS[name] +
    `</svg>`
  );
}
